<template>
  <div class="tech-multi-ac">
    <label v-if="props.label" class="form-label">{{ props.label }}</label>
    <tech-ac 
      :key="techSelectorKey"
      v-model="selectedTechForAdd" 
      :placeholder="props.placeholder"
      @change="addTechnician"
    />
    <div v-if="model.length > 0" class="d-flex flex-wrap gap-2 mt-2">
      <span 
        v-for="techId in model" 
        :key="techId"
        class="badge bg-primary d-flex align-items-center gap-2"
        style="font-size: 0.9rem; padding: 0.4rem 0.6rem;"
      >
        {{ getTechName(techId) }}
        <button 
          type="button" 
          class="btn-close btn-close-white" 
          style="font-size: 0.6rem;"
          @click="removeTechnician(techId)"
          aria-label="Remove"
        ></button>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, watch } from 'vue';
import TechAc from '@/components/Techs/TechAc.vue';
import { useTechStore } from '@/stores/TechStore';

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Select technician to add...'
  }
});

const model = defineModel<number[]>({
  default: () => []
});

const techStore = useTechStore();
const selectedTechForAdd = ref('');
const techSelectorKey = ref(0);

// Initialize tech store
onMounted(async () => {
  await techStore.refresh();
  console.log('TechMultiAc mounted, initial model:', model.value);
});

// Watch model changes
watch(model, (newVal, oldVal) => {
  console.log('Model changed from', oldVal, 'to', newVal);
}, { deep: true });

// Watch selectedTechForAdd and immediately process when it gets a value
watch(selectedTechForAdd, async (newVal) => {
  console.log('selectedTechForAdd changed to:', newVal, 'type:', typeof newVal);
  
  if (!newVal) return;
  
  // Convert to string if it's a number
  const techIdStr = String(newVal);
  const id = parseInt(techIdStr);
  
  console.log('Processing tech ID:', id, 'Current model:', model.value);
  
  if (!isNaN(id) && !model.value.includes(id)) {
    // Create new array to ensure reactivity
    model.value = [...model.value, id];
    console.log('Added tech, new model:', model.value);
  }
  
  // Immediately reset the selector by incrementing key
  // This forces TechAc to recreate with empty v-model
  await nextTick();
  techSelectorKey.value++;
  selectedTechForAdd.value = '';
  console.log('Selector reset, new key:', techSelectorKey.value);
});

// Add technician to selection (keeping this for the @change event, but watcher does the work)
const addTechnician = async (techId: string) => {
  // This may not be needed anymore since the watcher handles it
  console.log('addTechnician @change event:', techId);
};

// Remove technician from selection
const removeTechnician = (techId: number) => {
  model.value = model.value.filter(id => id !== techId);
};

// Get tech name from store
const getTechName = (techId: number): string => {
  const tech = techStore.list[techId];
  return tech?.TechnicianName || 'Unknown';
};
</script>

<style scoped>
.tech-multi-ac {
  width: 100%;
}
</style>
